import React, { useState } from 'react';
import { CommandCenterLayout } from '../../components/layout/CommandCenterLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { DataTable } from '../../components/ui/DataTable';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { KPICard } from '../../components/ui/KPICard';
import { FilterChip } from '../../components/ui/FilterChip';
import { useAuth, PermissionGate } from '../../lib/auth';
import { FileText, Clock, Zap, CheckCircle, Plus, Search, Filter, Radio, Users, Shield, MoreHorizontal, ExternalLink, Image as ImageIcon, AlertTriangle, MessageSquare, Send, Eye } from 'lucide-react';
// Types
type ReportSource = 'pemantauan_sosmed' | 'hasil_rapat' | 'tim_wcc' | 'lainnya';
type ReportPlatform = 'Instagram' | 'TikTok' | 'X' | 'Facebook' | 'YouTube' | 'Multi';
type ReportSentiment = 'positif' | 'netral' | 'negatif';
type ReportUrgency = 'Low' | 'Medium' | 'High' | 'Urgent';
type ReportStatus = 'new' | 'in_treatment' | 'completed' | 'archived';
interface ReportItem {
  id: string;
  source: ReportSource;
  title: string;
  description: string;
  platform: ReportPlatform;
  sentiment: ReportSentiment;
  urgency: ReportUrgency;
  status: ReportStatus;
  date: string;
  reporter: string;
  link?: string;
  treatment?: {
    type: string;
    assignedTo: string;
    deadline: string;
    note: string;
    status: 'in_progress' | 'done';
  };
  timeline: Array<{
    time: string;
    action: string;
    user: string;
  }>;
}
// Mock Data
const mockReports: ReportItem[] = [{
  id: 'PMT-001',
  source: 'pemantauan_sosmed',
  title: 'Viral Tumpukan Sampah di Pasar Minggu',
  description: 'Banyak netizen mengeluhkan tumpukan sampah yang belum diangkut selama 3 hari di area belakang Pasar Minggu. Video viral di TikTok dengan 50k views.',
  platform: 'TikTok',
  sentiment: 'negatif',
  urgency: 'High',
  status: 'new',
  date: '2025-02-14 09:30',
  reporter: 'Admin Sosmed',
  link: 'https://tiktok.com/example',
  timeline: [{
    time: '2025-02-14 09:30',
    action: 'Laporan dibuat',
    user: 'Admin Sosmed'
  }]
}, {
  id: 'PMT-002',
  source: 'hasil_rapat',
  title: 'Arahan Menteri terkait HPSN 2025',
  description: 'Hasil rapat pimpinan: Perlu dibuatkan konten series edukasi menyambut HPSN 2025 dengan fokus pada pengurangan plastik sekali pakai.',
  platform: 'Multi',
  sentiment: 'netral',
  urgency: 'Medium',
  status: 'in_treatment',
  date: '2025-02-13 14:00',
  reporter: 'Notulen Rapat',
  treatment: {
    type: 'Buat Counter Content',
    assignedTo: 'Tim Konten',
    deadline: '2025-02-20',
    note: 'Segera buat draft storyboard untuk 3 video pendek.',
    status: 'in_progress'
  },
  timeline: [{
    time: '2025-02-13 14:00',
    action: 'Laporan dibuat',
    user: 'Notulen Rapat'
  }, {
    time: '2025-02-13 15:30',
    action: 'Treatment dimulai',
    user: 'Koordinator'
  }]
}, {
  id: 'PMT-003',
  source: 'tim_wcc',
  title: 'Isu Pencemaran Sungai Citarum',
  description: 'Laporan masuk dari WCC mengenai indikasi pembuangan limbah pabrik di sektor 4 Citarum. Perlu monitoring media sosial untuk melihat respon publik.',
  platform: 'X',
  sentiment: 'negatif',
  urgency: 'Urgent',
  status: 'new',
  date: '2025-02-14 08:15',
  reporter: 'Officer WCC',
  timeline: [{
    time: '2025-02-14 08:15',
    action: 'Laporan dibuat',
    user: 'Officer WCC'
  }]
}, {
  id: 'PMT-004',
  source: 'pemantauan_sosmed',
  title: 'Apresiasi Warga Program Bank Sampah',
  description: 'Banyak respon positif di Instagram terkait program penukaran sampah jadi emas di Bank Sampah Induk Jakarta Barat.',
  platform: 'Instagram',
  sentiment: 'positif',
  urgency: 'Low',
  status: 'completed',
  date: '2025-02-12 10:00',
  reporter: 'Admin Sosmed',
  treatment: {
    type: 'Buat Follow-up Content',
    assignedTo: 'Tim Humas',
    deadline: '2025-02-13',
    note: 'Repost testimoni warga dan buat highlight story.',
    status: 'done'
  },
  timeline: [{
    time: '2025-02-12 10:00',
    action: 'Laporan dibuat',
    user: 'Admin Sosmed'
  }, {
    time: '2025-02-12 11:00',
    action: 'Treatment selesai',
    user: 'Tim Humas'
  }]
}, {
  id: 'PMT-005',
  source: 'lainnya',
  title: 'Hoax Larangan Penggunaan Kantong Plastik',
  description: 'Beredar informasi salah bahwa penggunaan kantong plastik akan didenda 500rb per lembar mulai besok. Perlu klarifikasi segera.',
  platform: 'Facebook',
  sentiment: 'negatif',
  urgency: 'High',
  status: 'in_treatment',
  date: '2025-02-14 11:00',
  reporter: 'Tim Monitoring',
  treatment: {
    type: 'Klarifikasi Resmi',
    assignedTo: 'Tim Humas',
    deadline: '2025-02-14',
    note: 'Buat press release dan infografis bantahan.',
    status: 'in_progress'
  },
  timeline: [{
    time: '2025-02-14 11:00',
    action: 'Laporan dibuat',
    user: 'Tim Monitoring'
  }, {
    time: '2025-02-14 11:15',
    action: 'Treatment dimulai',
    user: 'Koordinator'
  }]
}, {
  id: 'PMT-006',
  source: 'pemantauan_sosmed',
  title: 'Video Edukasi Kompos Viral',
  description: 'Video tutorial kompos dari kreator lokal viral. Potensi kolaborasi untuk kampanye nasional.',
  platform: 'YouTube',
  sentiment: 'positif',
  urgency: 'Medium',
  status: 'new',
  date: '2025-02-13 16:45',
  reporter: 'Admin Sosmed',
  timeline: [{
    time: '2025-02-13 16:45',
    action: 'Laporan dibuat',
    user: 'Admin Sosmed'
  }]
}, {
  id: 'PMT-007',
  source: 'hasil_rapat',
  title: 'Evaluasi Kinerja Tim Sosmed Januari',
  description: 'Catatan evaluasi bulanan: Engagement rate turun 5% di Instagram. Perlu strategi konten baru.',
  platform: 'Instagram',
  sentiment: 'netral',
  urgency: 'Low',
  status: 'archived',
  date: '2025-02-01 09:00',
  reporter: 'Manager Sosmed',
  timeline: [{
    time: '2025-02-01 09:00',
    action: 'Diarsipkan',
    user: 'System'
  }]
}, {
  id: 'PMT-008',
  source: 'tim_wcc',
  title: 'Keluhan Bau TPA Regional',
  description: 'Laporan warga masuk ke Call Center mengenai bau menyengat dari TPA Regional saat hujan.',
  platform: 'Multi',
  sentiment: 'negatif',
  urgency: 'High',
  status: 'new',
  date: '2025-02-14 07:30',
  reporter: 'Operator CC',
  timeline: [{
    time: '2025-02-14 07:30',
    action: 'Laporan dibuat',
    user: 'Operator CC'
  }]
}];
export function PemantauanSosmedPage() {
  const {
    user
  } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportItem | null>(null);
  // Filter Data
  const filteredData = mockReports.filter(item => {
    const matchesTab = activeTab === 'all' || activeTab === 'new' && item.status === 'new' || activeTab === 'treatment' && item.status === 'in_treatment' || activeTab === 'completed' && item.status === 'completed' || activeTab === 'archived' && item.status === 'archived';
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase()) || item.id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });
  // Helper Functions
  const getSourceIcon = (source: ReportSource) => {
    switch (source) {
      case 'pemantauan_sosmed':
        return <Radio className="h-4 w-4 text-purple-500" data-id="element-1926" />;
      case 'hasil_rapat':
        return <Users className="h-4 w-4 text-blue-500" data-id="element-1927" />;
      case 'tim_wcc':
        return <Shield className="h-4 w-4 text-orange-500" data-id="element-1928" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" data-id="element-1929" />;
    }
  };
  const getSourceLabel = (source: ReportSource) => {
    switch (source) {
      case 'pemantauan_sosmed':
        return 'Sosmed Monitoring';
      case 'hasil_rapat':
        return 'Hasil Rapat';
      case 'tim_wcc':
        return 'Tim WCC';
      default:
        return 'Lainnya';
    }
  };
  const getPlatformBadge = (platform: ReportPlatform) => {
    const colors: Record<ReportPlatform, string> = {
      Instagram: 'bg-pink-100 text-pink-700 border-pink-200',
      TikTok: 'bg-black text-white border-gray-800',
      X: 'bg-gray-900 text-white border-gray-700',
      Facebook: 'bg-blue-100 text-blue-700 border-blue-200',
      YouTube: 'bg-red-100 text-red-700 border-red-200',
      Multi: 'bg-purple-100 text-purple-700 border-purple-200'
    };
    return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${colors[platform]}`} data-id="element-1930">
        {platform}
      </span>;
  };
  const getSentimentBadge = (sentiment: ReportSentiment) => {
    switch (sentiment) {
      case 'positif':
        return <Badge variant="success" className="text-[10px]" data-id="element-1931">
            Positif
          </Badge>;
      case 'negatif':
        return <Badge variant="destructive" className="text-[10px]" data-id="element-1932">
            Negatif
          </Badge>;
      default:
        return <Badge variant="secondary" className="text-[10px]" data-id="element-1933">
            Netral
          </Badge>;
    }
  };
  const getUrgencyBadge = (urgency: ReportUrgency) => {
    switch (urgency) {
      case 'Urgent':
        return <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded font-bold border border-red-200" data-id="element-1934">
            URGENT
          </span>;
      case 'High':
        return <span className="bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded font-medium border border-orange-200" data-id="element-1935">
            High
          </span>;
      case 'Medium':
        return <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded font-medium border border-yellow-200" data-id="element-1936">
            Medium
          </span>;
      default:
        return <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded font-medium border border-gray-200" data-id="element-1937">
            Low
          </span>;
    }
  };
  const getStatusBadge = (status: ReportStatus) => {
    switch (status) {
      case 'new':
        return <StatusBadge status="kuning" data-id="element-1938">Baru</StatusBadge>;
      case 'in_treatment':
        return <StatusBadge status="hijau" data-id="element-1939">Dalam Treatment</StatusBadge>;
      case 'completed':
        return <StatusBadge status="abu" data-id="element-1940">Selesai</StatusBadge>;
      case 'archived':
        return <StatusBadge status="merah" data-id="element-1941">Arsip</StatusBadge>;
    }
  };
  // Table Columns
  const columns = [{
    header: 'ID',
    cell: (row: ReportItem) => <span className="font-mono text-xs font-medium text-gray-500" data-id="element-1942">
          {row.id}
        </span>
  }, {
    header: 'Sumber',
    cell: (row: ReportItem) => <div className="flex items-center gap-2" title={getSourceLabel(row.source)} data-id="element-1943">
          {getSourceIcon(row.source)}
          <span className="text-sm text-gray-700 hidden md:inline" data-id="element-1944">
            {getSourceLabel(row.source)}
          </span>
        </div>
  }, {
    header: 'Judul Isu',
    cell: (row: ReportItem) => <div className="max-w-[250px]" data-id="element-1945">
          <div className="font-medium text-gray-900 line-clamp-1" data-id="element-1946">
            {row.title}
          </div>
          <div className="text-xs text-gray-500 line-clamp-1" data-id="element-1947">
            {row.description}
          </div>
        </div>
  }, {
    header: 'Platform',
    cell: (row: ReportItem) => getPlatformBadge(row.platform)
  }, {
    header: 'Sentimen',
    cell: (row: ReportItem) => getSentimentBadge(row.sentiment)
  }, {
    header: 'Urgensi',
    cell: (row: ReportItem) => getUrgencyBadge(row.urgency)
  }, {
    header: 'Status',
    cell: (row: ReportItem) => getStatusBadge(row.status)
  }, {
    header: 'Aksi',
    cell: (row: ReportItem) => <Button variant="ghost" size="sm" className="text-primary-600 hover:text-primary-700 hover:bg-primary-50" onClick={() => setSelectedReport(row)} data-id="element-1948">
          {row.status === 'new' ? 'Proses' : 'Lihat'}
        </Button>
  }];
  return <CommandCenterLayout title="Laporan Pemantauan Sosmed" subtitle="Monitoring isu, hasil rapat, dan temuan lapangan untuk tindak lanjut konten" data-id="element-1949">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6" data-id="element-1950">
        <KPICard title="Total Laporan Masuk" value="156" icon={FileText} trend={{
        value: '+12 minggu ini',
        direction: 'up'
      }} data-id="element-1951" />
        <KPICard title="Menunggu Treatment" value="23" icon={Clock} className="border-l-4 border-l-orange-500" data-id="element-1952" />
        <KPICard title="Dalam Proses" value="18" icon={Zap} className="border-l-4 border-l-blue-500" data-id="element-1953" />
        <KPICard title="Selesai / Konten Dibuat" value="115" icon={CheckCircle} className="border-l-4 border-l-green-500" data-id="element-1954" />
      </div>

      <div className="flex flex-col space-y-4 mb-6" data-id="element-1955">
        {/* Tabs */}
        <div className="flex gap-2 border-b pb-0 overflow-x-auto no-scrollbar" data-id="element-1956">
          {[{
          id: 'all',
          label: 'Semua'
        }, {
          id: 'new',
          label: 'Baru'
        }, {
          id: 'treatment',
          label: 'Dalam Treatment'
        }, {
          id: 'completed',
          label: 'Selesai'
        }, {
          id: 'archived',
          label: 'Arsip'
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1957">
              {tab.label}
            </button>)}
        </div>

        {/* Filters & Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center" data-id="element-1958">
          <div className="flex flex-wrap gap-2 items-center w-full md:w-auto" data-id="element-1959">
            <div className="relative flex-1 md:w-64" data-id="element-1960">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-1961" />
              <Input placeholder="Cari isu, judul, atau ID..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-id="element-1962" />
            </div>
            <Button variant="outline" className="gap-2" data-id="element-1963">
              <Filter className="h-4 w-4" data-id="element-1964" /> Filter
            </Button>
          </div>

          <PermissionGate permission="canManageContent" data-id="element-1965">
            <Button className="bg-primary-600 hover:bg-primary-700 text-white gap-2 w-full md:w-auto" onClick={() => setIsCreateModalOpen(true)} data-id="element-1966">
              <Plus className="h-4 w-4" data-id="element-1967" /> Buat Laporan
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0" data-id="element-1968">
        <DataTable data={filteredData} columns={columns} keyExtractor={item => item.id} onRowClick={item => setSelectedReport(item)} data-id="element-1969" />
      </div>

      {/* Create Report Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Buat Laporan Pemantauan Baru" size="lg" footer={<div className="flex justify-end gap-2 w-full" data-id="element-1971">
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)} data-id="element-1972">
              Batal
            </Button>
            <Button variant="outline" data-id="element-1973">Simpan Draft</Button>
            <Button className="bg-primary-600 text-white" data-id="element-1974">
              Submit Laporan
            </Button>
          </div>} data-id="element-1970">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1975">
          <div className="space-y-2" data-id="element-1976">
            <label className="text-sm font-medium text-gray-700" data-id="element-1977">
              Sumber Laporan <span className="text-red-500" data-id="element-1978">*</span>
            </label>
            <Select data-id="element-1979">
              <option value="pemantauan_sosmed" data-id="element-1980">Pemantauan Sosmed</option>
              <option value="hasil_rapat" data-id="element-1981">Hasil Rapat</option>
              <option value="tim_wcc" data-id="element-1982">Tim WCC</option>
              <option value="lainnya" data-id="element-1983">Lainnya</option>
            </Select>
          </div>

          <div className="space-y-2" data-id="element-1984">
            <label className="text-sm font-medium text-gray-700" data-id="element-1985">
              Platform <span className="text-red-500" data-id="element-1986">*</span>
            </label>
            <Select data-id="element-1987">
              <option value="Instagram" data-id="element-1988">Instagram</option>
              <option value="TikTok" data-id="element-1989">TikTok</option>
              <option value="X" data-id="element-1990">X (Twitter)</option>
              <option value="Facebook" data-id="element-1991">Facebook</option>
              <option value="YouTube" data-id="element-1992">YouTube</option>
              <option value="Multi" data-id="element-1993">Multi-platform</option>
            </Select>
          </div>

          <div className="col-span-1 md:col-span-2 space-y-2" data-id="element-1994">
            <label className="text-sm font-medium text-gray-700" data-id="element-1995">
              Judul Isu / Temuan <span className="text-red-500" data-id="element-1996">*</span>
            </label>
            <Input placeholder="Contoh: Viral Tumpukan Sampah di Pasar Minggu" data-id="element-1997" />
          </div>

          <div className="col-span-1 md:col-span-2 space-y-2" data-id="element-1998">
            <label className="text-sm font-medium text-gray-700" data-id="element-1999">
              Deskripsi Temuan <span className="text-red-500" data-id="element-2000">*</span>
            </label>
            <Textarea placeholder="Jelaskan detail temuan, konteks, dan respon publik..." className="h-24" data-id="element-2001" />
          </div>

          <div className="col-span-1 md:col-span-2 space-y-2" data-id="element-2002">
            <label className="text-sm font-medium text-gray-700" data-id="element-2003">
              Link Bukti / URL Referensi
            </label>
            <div className="relative" data-id="element-2004">
              <ExternalLink className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-2005" />
              <Input placeholder="https://..." className="pl-9" data-id="element-2006" />
            </div>
          </div>

          <div className="col-span-1 md:col-span-2 space-y-2" data-id="element-2007">
            <label className="text-sm font-medium text-gray-700" data-id="element-2008">
              Screenshot / Lampiran
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-2009">
              <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" data-id="element-2010" />
              <p className="text-sm text-gray-600" data-id="element-2011">
                Klik untuk upload atau drag & drop
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-2012">
                JPG, PNG, PDF (Max. 5MB)
              </p>
            </div>
          </div>

          <div className="space-y-2" data-id="element-2013">
            <label className="text-sm font-medium text-gray-700" data-id="element-2014">
              Sentimen Awal
            </label>
            <Select data-id="element-2015">
              <option value="netral" data-id="element-2016">Netral</option>
              <option value="positif" data-id="element-2017">Positif</option>
              <option value="negatif" data-id="element-2018">Negatif</option>
            </Select>
          </div>

          <div className="space-y-2" data-id="element-2019">
            <label className="text-sm font-medium text-gray-700" data-id="element-2020">Urgensi</label>
            <Select data-id="element-2021">
              <option value="Low" data-id="element-2022">Low</option>
              <option value="Medium" data-id="element-2023">Medium</option>
              <option value="High" data-id="element-2024">High</option>
              <option value="Urgent" data-id="element-2025">Urgent</option>
            </Select>
          </div>

          <div className="col-span-1 md:col-span-2 space-y-2" data-id="element-2026">
            <label className="text-sm font-medium text-gray-700" data-id="element-2027">
              Rekomendasi Awal (Opsional)
            </label>
            <Textarea placeholder="Saran tindak lanjut..." className="h-20" data-id="element-2028" />
          </div>
        </div>
      </Modal>

      {/* Detail & Treatment Modal */}
      <Modal isOpen={!!selectedReport} onClose={() => setSelectedReport(null)} title={`Detail Laporan #${selectedReport?.id}`} size="2xl" data-id="element-2029">
        {selectedReport && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2030">
            {/* Left Column: Info */}
            <div className="lg:col-span-2 space-y-6" data-id="element-2031">
              <div className="p-4 bg-gray-50 rounded-lg border" data-id="element-2032">
                <div className="flex justify-between items-start mb-3" data-id="element-2033">
                  <div className="flex items-center gap-2" data-id="element-2034">
                    {getSourceIcon(selectedReport.source)}
                    <span className="text-sm font-medium text-gray-700 capitalize" data-id="element-2035">
                      {getSourceLabel(selectedReport.source)}
                    </span>
                  </div>
                  <div className="flex gap-2" data-id="element-2036">
                    {getPlatformBadge(selectedReport.platform)}
                    {getUrgencyBadge(selectedReport.urgency)}
                  </div>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2" data-id="element-2037">
                  {selectedReport.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4" data-id="element-2038">
                  {selectedReport.description}
                </p>
                <div className="flex items-center gap-4 text-xs text-gray-500 border-t pt-3" data-id="element-2039">
                  <span className="flex items-center gap-1" data-id="element-2040">
                    <Clock className="h-3 w-3" data-id="element-2041" /> {selectedReport.date}
                  </span>
                  <span className="flex items-center gap-1" data-id="element-2042">
                    <Users className="h-3 w-3" data-id="element-2043" /> {selectedReport.reporter}
                  </span>
                  <span className="flex items-center gap-1" data-id="element-2044">
                    <MessageSquare className="h-3 w-3" data-id="element-2045" /> Sentimen:{' '}
                    {selectedReport.sentiment}
                  </span>
                </div>
              </div>

              {selectedReport.link && <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg text-blue-700 text-sm" data-id="element-2046">
                  <ExternalLink className="h-4 w-4 flex-shrink-0" data-id="element-2047" />
                  <a href={selectedReport.link} target="_blank" rel="noreferrer" className="hover:underline truncate" data-id="element-2048">
                    {selectedReport.link}
                  </a>
                </div>}

              <div data-id="element-2049">
                <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2050">
                  Bukti / Lampiran
                </h4>
                <div className="aspect-video bg-gray-100 rounded-lg border flex items-center justify-center text-gray-400" data-id="element-2051">
                  <div className="text-center" data-id="element-2052">
                    <ImageIcon className="h-8 w-8 mx-auto mb-1" data-id="element-2053" />
                    <span className="text-xs" data-id="element-2054">Preview Image Placeholder</span>
                  </div>
                </div>
              </div>

              <div data-id="element-2055">
                <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-2056">
                  Audit Trail
                </h4>
                <div className="space-y-4 relative pl-4 border-l" data-id="element-2057">
                  {selectedReport.timeline.map((event, i) => <div key={i} className="relative" data-id="element-2058">
                      <div className="absolute -left-[21px] top-1 h-3 w-3 rounded-full bg-primary-500 border-2 border-white" data-id="element-2059"></div>
                      <p className="text-sm font-medium text-gray-900" data-id="element-2060">
                        {event.action}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2061">
                        {event.time} • {event.user}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Right Column: Treatment Panel */}
            <div className="space-y-6" data-id="element-2062">
              <PermissionGate permission="canApproveReports" fallback={<div className="bg-gray-50 p-4 rounded-lg border text-center text-sm text-gray-500" data-id="element-2064">
                    Anda tidak memiliki akses untuk melakukan treatment laporan.
                  </div>} data-id="element-2063">
                {selectedReport.status === 'new' ? <div className="bg-white p-4 rounded-lg border shadow-sm space-y-4" data-id="element-2065">
                    <h4 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-2066">
                      <Zap className="h-4 w-4 text-blue-600" data-id="element-2067" />
                      Mulai Treatment
                    </h4>

                    <div className="space-y-3" data-id="element-2068">
                      <div data-id="element-2069">
                        <label className="text-xs font-medium text-gray-600" data-id="element-2070">
                          Jenis Treatment
                        </label>
                        <Select data-id="element-2071">
                          <option data-id="element-2072">Buat Counter Content</option>
                          <option data-id="element-2073">Buat Follow-up Content</option>
                          <option data-id="element-2074">Klarifikasi Resmi</option>
                          <option data-id="element-2075">Monitoring Lanjutan</option>
                          <option data-id="element-2076">Eskalasi ke Pimpinan</option>
                        </Select>
                      </div>

                      <div data-id="element-2077">
                        <label className="text-xs font-medium text-gray-600" data-id="element-2078">
                          Assign ke Tim
                        </label>
                        <Select data-id="element-2079">
                          <option data-id="element-2080">Tim Konten</option>
                          <option data-id="element-2081">Tim Humas</option>
                          <option data-id="element-2082">Tim WCC</option>
                          <option data-id="element-2083">Tim Sosmed</option>
                        </Select>
                      </div>

                      <div data-id="element-2084">
                        <label className="text-xs font-medium text-gray-600" data-id="element-2085">
                          Deadline
                        </label>
                        <Input type="date" data-id="element-2086" />
                      </div>

                      <div data-id="element-2087">
                        <label className="text-xs font-medium text-gray-600" data-id="element-2088">
                          Catatan Treatment
                        </label>
                        <Textarea placeholder="Instruksi detail..." className="h-20" data-id="element-2089" />
                      </div>

                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-id="element-2090">
                        Simpan & Mulai Treatment
                      </Button>
                    </div>
                  </div> : selectedReport.status === 'in_treatment' && selectedReport.treatment ? <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 space-y-4" data-id="element-2091">
                    <div className="flex justify-between items-start" data-id="element-2092">
                      <h4 className="font-bold text-blue-900 flex items-center gap-2" data-id="element-2093">
                        <Zap className="h-4 w-4" data-id="element-2094" />
                        Dalam Proses
                      </h4>
                      <Badge variant="outline" className="bg-white text-blue-700 border-blue-200" data-id="element-2095">
                        {selectedReport.treatment.assignedTo}
                      </Badge>
                    </div>

                    <div className="text-sm text-blue-800 space-y-2" data-id="element-2096">
                      <p data-id="element-2097">
                        <strong data-id="element-2098">Action:</strong> {selectedReport.treatment.type}
                      </p>
                      <p data-id="element-2099">
                        <strong data-id="element-2100">Deadline:</strong>{' '}
                        {selectedReport.treatment.deadline}
                      </p>
                      <p className="bg-white p-2 rounded border border-blue-100 text-xs" data-id="element-2101">
                        "{selectedReport.treatment.note}"
                      </p>
                    </div>

                    <div className="space-y-2 pt-2" data-id="element-2102">
                      <Button className="w-full bg-green-600 hover:bg-green-700 text-white" data-id="element-2103">
                        <CheckCircle className="h-4 w-4 mr-2" data-id="element-2104" />
                        Tandai Selesai
                      </Button>
                      <Button variant="outline" className="w-full bg-white text-blue-700 border-blue-200 hover:bg-blue-50" data-id="element-2105">
                        <FileText className="h-4 w-4 mr-2" data-id="element-2106" />
                        Buat Konten Terkait
                      </Button>
                    </div>
                  </div> : <div className="bg-green-50 p-4 rounded-lg border border-green-100 text-center" data-id="element-2107">
                    <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" data-id="element-2108" />
                    <h4 className="font-bold text-green-900" data-id="element-2109">
                      Laporan Selesai
                    </h4>
                    <p className="text-sm text-green-700 mt-1" data-id="element-2110">
                      Treatment telah diselesaikan oleh{' '}
                      {selectedReport.treatment?.assignedTo}
                    </p>
                  </div>}
              </PermissionGate>
            </div>
          </div>}
      </Modal>
    </CommandCenterLayout>;
}